import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  Download,
} from "@mui/icons-material";
import PdfRenderer from "components/preview/renderers/PdfRenderer";
import { formatBytes } from "components/preview/previewTypes";
import { startDownloadViaHiddenFrame } from "utils/hiddenFrameDownload";
import { useLazyGetPdfJobFileUrlQuery } from "features/pdfTools/pdfTools";

export interface ViewerFile {
  name: string;
  key: string;
  size?: number;
}

interface WorkspaceFileViewerProps {
  jobId: string;
  files: ViewerFile[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export default function WorkspaceFileViewer({
  jobId,
  files,
  index,
  onClose,
  onIndexChange,
}: WorkspaceFileViewerProps) {
  const [getFileUrl] = useLazyGetPdfJobFileUrlQuery();
  const fullScreen = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestSeq = useRef(0);

  const file = files[index] ?? null;
  const hasPrev = index > 0;
  const hasNext = index < files.length - 1;

  useEffect(() => {
    if (!file) return undefined;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    setBlob(null);

    (async () => {
      try {
        const { url } = await getFileUrl({ jobId, key: file.key }).unwrap();
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.blob();
        if (requestSeq.current === seq) setBlob(data);
      } catch {
        if (requestSeq.current === seq) {
          setError(`Could not load "${file.name}".`);
        }
      } finally {
        if (requestSeq.current === seq) setLoading(false);
      }
    })();

    return undefined;
  }, [file, jobId, getFileUrl]);

  const blobUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : ""), [blob]);
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const goPrev = useCallback(() => {
    if (hasPrev) onIndexChange(index - 1);
  }, [hasPrev, index, onIndexChange]);
  const goNext = useCallback(() => {
    if (hasNext) onIndexChange(index + 1);
  }, [hasNext, index, onIndexChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      goPrev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      goNext();
    }
  };

  const handleDownload = async () => {
    if (!file) return;
    try {
      const { url } = await getFileUrl({ jobId, key: file.key }).unwrap();
      startDownloadViaHiddenFrame(url);
    } catch {
      setError(`Could not download "${file.name}".`);
    }
  };

  if (!file) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      onKeyDown={handleKeyDown}
      PaperProps={{ sx: { height: fullScreen ? "100%" : "88vh" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap title={file.name} sx={{ fontWeight: 700 }}>
            {file.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            File {index + 1} of {files.length}
            {file.size != null ? ` - ${formatBytes(file.size)}` : ""}
            {fullScreen ? "" : " - use the arrow keys to browse"}
          </Typography>
        </Box>
        <Tooltip title="Previous file (left arrow)">
          <span>
            <IconButton onClick={goPrev} disabled={!hasPrev}>
              <ChevronLeft />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Next file (right arrow)">
          <span>
            <IconButton onClick={goNext} disabled={!hasNext}>
              <ChevronRight />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton onClick={() => void handleDownload()}>
            <Download />
          </IconButton>
        </Tooltip>
        <IconButton onClick={onClose} aria-label="Close viewer">
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, bgcolor: "action.hover" }}>
        {loading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : blob && blobUrl ? (
          <PdfRenderer blob={blob} blobUrl={blobUrl} fileName={file.name} />
        ) : null}
      </Box>
    </Dialog>
  );
}
